import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

interface Update {
  id: string;
  title: string;
  category: string;
  image: string;
  content: string;
  date: string;
  author: string;
}

const UpdateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [update, setUpdate] = useState<Update | null>(null);
  const [recentUpdates, setRecentUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch update from API
    if (id) {
      fetch(`http://localhost:5000/api/updates/${id}`)
        .then(res => res.json())
        .then(data => {
          setUpdate(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching update:', error);
          setLoading(false);
        });
    }

    // Fetch recent updates for sidebar
    fetch('http://localhost:5000/api/updates?limit=5')
      .then(res => res.json())
      .then(data => {
        // Filter out the current update from recent updates
        const filtered = data.filter((item: Update) => item.id !== id);
        setRecentUpdates(filtered.slice(0, 3));
      })
      .catch(error => {
        console.error('Error fetching recent updates:', error);
      });
  }, [id]);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }
  }, [update]);

  if (!update) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* Page banner area start here */}
      <section className="banner__inner-page bg-image pt-160 pb-160 bg-image"
        data-background="/assets/images/banner/banner-inner-page.jpg">
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Update Details</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i></span>
            <Link to="/updates">Updates</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Update Details</span>
          </div>
        </div>
      </section>
      {/* Page banner area end here */}

      {loading ? (
        <div className="text-center py-5">
          <p>Loading update...</p>
        </div>
      ) : !update ? (
        <div className="text-center py-5">
          <h3>Update not found</h3>
          <Link to="/updates" className="btn-one mt-3">
            <span>Back to Updates</span>
          </Link>
        </div>
      ) : (
        <>
          {/* Blog single area start here */}
          <section className="blog-single-area pt-120 pb-120">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8 order-2 order-lg-1">
              <div className="blog__item blog-single__left-item shadow-none">
                <div className="image">
                  <img src={update.image} alt={update.title} />
                </div>
                <div className="blog__content p-0">
                  <div className="blog-meta mt-20 mb-20">
                    <span className="blog-tag">{update.category}</span>
                    <span className="ms-3">
                      <i className="fa-regular fa-calendar me-1"></i>
                      {update.date}
                    </span>
                    <span className="ms-3">
                      <i className="fa-regular fa-user me-1"></i>
                      {update.author}
                    </span>
                  </div>
                  <h4 className="mt-20 mb-20 fs-30">{update.title}</h4>
                  <div dangerouslySetInnerHTML={{ __html: update.content }} />
                  
                  <div className="share-area mt-40 pt-40 bor-top">
                    <h5 className="mb-20">Share This Update</h5>
                    <ul className="social-icon">
                      <li><a href="#0"><i className="fa-brands fa-facebook-f"></i></a></li>
                      <li><a href="#0"><i className="fa-brands fa-twitter"></i></a></li>
                      <li><a href="#0"><i className="fa-brands fa-linkedin-in"></i></a></li>
                      <li><a href="#0"><i className="fa-brands fa-pinterest-p"></i></a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 order-1 order-lg-2">
              <div className="blog-single__right-item">
                <div className="item shadow mb-30">
                  <h5 className="title">Categories</h5>
                  <ul className="category">
                    <li><Link to="/updates?category=education">Education</Link> <span>(6)</span></li>
                    <li><Link to="/updates?category=healthcare">Healthcare</Link> <span>(4)</span></li>
                    <li><Link to="/updates?category=community">Community</Link> <span>(5)</span></li>
                    <li><Link to="/updates?category=women">Women's Empowerment</Link> <span>(3)</span></li>
                  </ul>
                </div>
                <div className="item shadow mb-30">
                  <h5 className="title">Recent Updates</h5>
                  <ul className="single-post">
                    {recentUpdates.map((recentUpdate) => (
                      <li key={recentUpdate.id}>
                        <img src={recentUpdate.image} alt={recentUpdate.title} style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                        <div className="con">
                          <h6><Link to={`/updates/${recentUpdate.id}`} className="primary-hover">{recentUpdate.title}</Link></h6>
                          <span>{new Date(recentUpdate.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="item shadow">
                  <h5 className="title">Tags</h5>
                  <div className="tags">
                    <Link to="/updates?tag=education">Education</Link>
                    <Link to="/updates?tag=healthcare">Healthcare</Link>
                    <Link to="/updates?tag=community">Community</Link>
                    <Link to="/updates?tag=children">Children</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Update single area end here */}
        </>
      )}
    </>
  );
};

export default UpdateDetail;
